import { styled, useTheme } from '@mui/material';
import { Grid } from '@mui/material';
import { AppTextInput, FormLabel } from 'components/FormElements';
import { Control, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import HorizontalLabelInputGroup from 'components/HorizontalLabelInputGroup';
import { realisation as rules } from 'further-ui/validations';
import useConsiderationCalculations, {
  ConsiderationPerUnitType,
  ConsiderationTotalType,
} from '../../hooks/useConsiderationCalculations';

type Props = {
  control: Control<rules.CreateRealisationFormValues>;
  index: number;
  currencySymbol: string;
  disabled: boolean;
};

const CenteredFormLabel = styled(FormLabel)(({ theme }) => ({
  whiteSpace: 'nowrap',
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: {
    marginBottom: theme.spacing(2),
  },
}));

const ReceivedConsiderationRow: React.FC<Props> = ({
  control,
  index,
  currencySymbol,
  disabled,
}) => {
  const theme = useTheme();
  const {
    onProRataPerUnitConsiderationChange,
    onProRataTotalConsiderationChange,
    setFieldFocus,
  } = useConsiderationCalculations();

  return (
    <Grid container spacing={3}>
      <Grid size={{ md: 5, xs: 12 }}>
        <CenteredFormLabel
          title="Received consideration"
          labelColor={theme.palette.text.secondary}
          tooltipText="This is the consideration physically received by the fund following the realisation. If there is withholding tax accounted for at the fund level, please do not include it here. Instead, add it in 'Withholding tax payable' below."
        />
      </Grid>
      <Grid container size={{ md: 7, xs: 12 }}>
        <HorizontalLabelInputGroup
          label="Per unit"
          labelColor={theme.palette.text.secondary}
          size={6}
          fullWidth
        >
          <Controller
            name={`consideration.${index}.proRataConsideration.receivedConsiderationPerUnit`}
            control={control}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error },
            }) => (
              <NumericFormat
                {...rest}
                disabled={disabled}
                inputRef={ref}
                onValueChange={({ value }) => {
                  onChange(+value);
                  onProRataPerUnitConsiderationChange(
                    index,
                    +value,
                    ConsiderationPerUnitType.Received,
                  );
                }}
                onFocus={() => {
                  setFieldFocus(ConsiderationPerUnitType.Received);
                }}
                prefix={currencySymbol}
                decimalScale={10}
                fullWidth
                allowNegative={false}
                thousandSeparator={true}
                customInput={AppTextInput}
                error={!!error}
                helperText={error?.message}
                size="small"
              />
            )}
          />
        </HorizontalLabelInputGroup>

        <HorizontalLabelInputGroup
          label="Total"
          labelColor={theme.palette.text.secondary}
          size={6}
          fullWidth
        >
          <Controller
            name={`consideration.${index}.proRataConsideration.receivedConsiderationTotal`}
            control={control}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error },
            }) => (
              <NumericFormat
                {...rest}
                disabled={disabled}
                inputRef={ref}
                onValueChange={({ value }) => {
                  onChange(+value);
                  onProRataTotalConsiderationChange(
                    index,
                    +value,
                    ConsiderationTotalType.Received,
                  );
                }}
                onFocus={() => {
                  setFieldFocus(ConsiderationTotalType.Received);
                }}
                prefix={currencySymbol}
                decimalScale={2}
                allowNegative={false}
                thousandSeparator={true}
                customInput={AppTextInput}
                error={!!error}
                helperText={error?.message}
                size="small"
              />
            )}
          />
        </HorizontalLabelInputGroup>
      </Grid>
    </Grid>
  );
};

export default ReceivedConsiderationRow;
